// api.js
// Centralized API + i18n helpers. No styling here.

/**
 * Extracts language code from the URL path.
 * Example: http://localhost:3000/pt/menu → "PT"
 */
export function getLangFromUrl(defaultLang = "EN") {
  try {
    const path = window.location.pathname.split("/").filter(Boolean);
    const langSegment = path[0]; // "pt"
    return langSegment ? langSegment.slice(0, 2).toUpperCase() : defaultLang;
  } catch {
    return defaultLang;
  }
}

/**
 * Normalize language codes (e.g., "pt-PT" -> "PT", "en_US" -> "EN")
 */
export function normalizeLangCode(code = "EN") {
  return String(code).slice(0, 2).toUpperCase();
}

/**
 * Safely picks a localized value from a typical array-of-objects format:
 * [{ language: { value: { code: "PT" } }, value: "..." }, ... ]
 */
export function getLocalizedValue(arr, langCode, fallback = "") {
  if (!Array.isArray(arr)) return fallback;
  const wanted = normalizeLangCode(langCode);
  const found = arr.find(
    (item) =>
      normalizeLangCode(item?.language?.value?.code || "") === wanted &&
      item?.value
  );
  if (found?.value) return found.value;

  // fallback: first with value, then explicit fallback
  const firstVal = arr.find((i) => i?.value)?.value;
  return firstVal ?? fallback;
}

/**
 * Generic fetch wrapper.
 */
async function request(url, opts = {}) {
  const res = await fetch(url, { mode: "cors", ...opts });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Request failed ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

const API_BASE = process.env.REACT_APP_CONNECTION_STRING;
const MENU = process.env.REACT_APP_ITEM_REPOSITORY;
const TYPES = process.env.REACT_APP_ITEM_TYPE_REPOSITORY;
const SLIDESHOW = process.env.REACT_APP_SLIDESHOW_REPOSITORY;

/**
 * Fetch menu items.
 */
export async function fetchMenu({ limit = 100 } = {}) {
  const url = `${API_BASE}/${MENU}?limit=${encodeURIComponent(limit)}`;
  const data = await request(url);
  return Array.isArray(data?.docs) ? data.docs : [];
}

/**
 * Fetch available item types.
 */
export async function fetchTypes() {
  const url = `${API_BASE}/${TYPES}`;
  const data = await request(url);
  return Array.isArray(data?.docs) ? data.docs : [];
}

/**
 * Fetch a slideshow by its ID.
 * Returns an object with `id`, `title`, and `images` array.
 */
export async function fetchSlideshow(id) {
  if (!id) throw new Error("No slideshow ID provided");
  const url = `${API_BASE}/${SLIDESHOW}/${id}`;
  return request(url);
}
